import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { SearchForecastDTO } from '@recurrency/core-api-schema/dist/search/getSearchForecasts';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import moment from 'moment';

import { FORECAST_COLOR, USAGE_COLOR } from 'pages/purchasing/utils';

import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { DEFAULT_NUM_ROWS_SHOWN, MiniTable } from 'components/MiniTable';
import {
  getUsageTableRowsFromForecastRecord,
  InventoryUsageForecastView,
  InventoryUsageForecastViewTab,
} from 'components/recipes/InventoryUsageForecastView';
import { ReportForecastTitle } from 'components/recipes/sidePane/ReportForecastTitle';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';
import { Sparkline } from 'components/Sparkline';
import { getMonthlyFilledSeries } from 'components/Sparkline/utils';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { usePromise } from 'hooks/usePromise';

import { showAsyncModal } from 'utils/asyncModal';
import { captureError } from 'utils/error';
import { formatIdNameObj } from 'utils/formatting';
import { searchIndex } from 'utils/search/search';

import { IdNameObj } from 'types/legacy-api';

import { getMergedForecastRecords, useItemInheritanceData } from './sidePane.utils';
import { UsageInheritanceInfo } from './UsageInheritanceInfo';

export function InventoryUsagePanelContent({
  itemId,
  itemUid,
  locations,
  purchaseGroupName,
  onForecastOverridesUpdated,
}: {
  itemId: string;
  itemUid: string;
  locations: IdNameObj[];
  purchaseGroupName?: string;
  onForecastOverridesUpdated?: () => void;
}) {
  const forecastRecordIds = locations.map((l) => `${itemUid}|${l.foreignId}`);
  const {
    data: searchIndexResponse,
    isLoading,
    error,
    reload: reloadForecastRecord,
  } = usePromise(
    () =>
      searchIndex<SearchForecastDTO>({
        indexName: SearchIndexName.Forecasts,
        filters: { id: forecastRecordIds },
      }),
    [itemId, locations],
    {
      cacheKey: `searchframe/${SearchIndexName.Forecasts}:${forecastRecordIds.join(',')}`,
      onError: captureError,
    },
  );

  const forecastRecords = searchIndexResponse?.hits;
  const forecastRecord = getMergedForecastRecords(forecastRecords);
  const usageTableRows = getUsageTableRowsFromForecastRecord(forecastRecord);
  const itemInheritanceData = useItemInheritanceData(forecastRecords, itemUid);

  const openUsageForecastModal = ({ initialTab }: { initialTab?: InventoryUsageForecastViewTab }) => {
    showAsyncModal(UsageForecastModal, {
      itemId,
      itemUid,
      locations,
      purchaseGroupName,
      forecastRecords,
      isLoading,
      initialTab,
      onForecastOverridesUpdated: () => {
        reloadForecastRecord();
        onForecastOverridesUpdated?.();
      },
    });
  };

  return (
    <>
      {forecastRecord && usageTableRows.length > 0 && !isLoading ? (
        <div>
          <div
            className={css`
              margin-bottom: 16px;
            `}
          >
            <UsageInheritanceInfo
              itemInheritanceData={itemInheritanceData}
              onEdit={() => openUsageForecastModal({ initialTab: InventoryUsageForecastViewTab.UsageInheritance })}
            />
          </div>
          <Sparkline
            className={css`
              margin-bottom: 12px;
            `}
            height={32}
            series={[
              ...getMonthlyFilledSeries({
                data: usageTableRows,
                dateField: 'date',
                valueField: 'usage',
                numPrevYears: 2,
                // Include current month to show both usage and forecast dots
                curMonthOffset: 0,
                title: 'Qty Used',
                formatFn: (value: number) => `${value} ${forecastRecord.unit_of_measure}`,
                color: USAGE_COLOR,
              }),
              {
                data: forecastRecord.forecast_dates.slice(0, 12 /* 12 month forecast */).map((_, i) => ({
                  date: forecastRecord.forecast_dates[i],
                  value: forecastRecord.forecast_demand[i] ?? 0,
                  dotted: true,
                })),
                title: 'Qty Forecasted',
                separation: true,
                formatFn: (value: number) => `${value} ${forecastRecord.unit_of_measure}`,
                color: FORECAST_COLOR,
              },
            ]}
          />
        </div>
      ) : null}
      <MiniTable
        columns={[
          { render: (record) => moment(record.date).format('YYYY MMMM') },
          { render: (record) => `${record.usage} ${record.unitOfMeasure || ``}`, align: 'right' },
        ]}
        data={usageTableRows}
        isLoading={isLoading}
        error={error}
        numRowsShown={DEFAULT_NUM_ROWS_SHOWN}
        onMoreButtonClick={() => openUsageForecastModal({})}
        moreButtonLabel={
          <>
            <EditOutlined /> View and Edit
          </>
        }
      />
    </>
  );
}

export function UsageForecastModal({
  itemId,
  itemUid,
  locations,
  purchaseGroupName,
  forecastRecords,
  isLoading,
  initialTab,
  onClose,
  onForecastOverridesUpdated,
}: {
  itemId: string;
  itemUid: string;
  locations: IdNameObj[];
  purchaseGroupName?: string;
  forecastRecords: SearchForecastDTO[] | undefined;
  isLoading: boolean;
  initialTab?: InventoryUsageForecastViewTab;
  onClose: () => void;
  onForecastOverridesUpdated?: () => void;
}) {
  return ViewAllModal({
    title: (
      <ReportForecastTitle
        title={
          <ConditionalWrapper
            condition={!!purchaseGroupName}
            wrapper={(children) => (
              <InfoTooltip useInfoIcon title={[locations?.map((sl, i) => <div key={i}>{sl.name}</div>)]}>
                {children}
              </InfoTooltip>
            )}
          >
            <>
              Usage & Forecast for Item #{itemId} at Location {purchaseGroupName ?? formatIdNameObj(locations[0])}
            </>
          </ConditionalWrapper>
        }
        itemId={itemId}
        itemUid={itemUid}
        locations={locations}
        forecastRecords={forecastRecords}
      />
    ),
    content: (
      <InventoryUsageForecastView
        forecastRecords={forecastRecords}
        itemUid={itemUid}
        locations={locations}
        isLoading={isLoading}
        initialTab={initialTab}
        onForecastOverridesUpdated={() => {
          onForecastOverridesUpdated?.();
        }}
      />
    ),
    onClose,
  });
}
